/*
  The cert-lookup results table (§3.4, §6.1, §6.4, D19, D22), rebuilt on the task-6
  primitives.

  Four things a reader should not have to reconstruct:

  - **It has no sort affordance at all (D19).** No column here declares `sortValue` or
    `compare`, and no `onSortChange` is passed, so neither the header nor the stacked
    control offers one. That is not an oversight: it renders ONE SERVER PAGE of ten rows
    at a time and `searchGradings` takes no sort parameter (`pcgPublicV2.ts`), so any
    control here would sort 10 of N rows while appearing to sort N.

  - **It skips `useTableState` entirely** — the server does the paging, so there is no
    client-side filter → sort → paginate pipeline to compose. It takes `page` and the
    server's `pagination.total` and drives `DataTablePagination` directly. The
    no-rows-per-page variant is simply omitting `onChangeRowsPerPage`: the page size is
    fixed at the hero's `rowsPerPage` and there is no size to offer.

  - **`offset`/`limit` were verified against the live endpoint, not assumed.** Task 9
    found the card views asking for `limit: 200` and getting all 1,532 rows. The cause is
    narrower than "the endpoint ignores limit": only the `gameCode` + `setName` +
    `cardNumber` triple — the card views' one-card lookup — bypasses it. Every shape this
    hero sends (basic mode's `cardName` + `gradedId` union, and advanced mode's fields
    with `useOr=false`) honours both, returns exactly `limit` rows and a correct
    `pagination.total`, and `offset` returns a genuinely different page. Advanced search
    has no card-number field, so the triple is unreachable from here.

  - **The grade cell goes through `finalGradeText`.** `GradingSearchResultDto.grade` is
    nullable in live data even though the type says it is not, and the template this
    replaces interpolated it straight into `{finalGrade} ({grade})` — painting "6 (null)".
*/

import { useMemo } from "react";

import {
  DataTable,
  DataTableColumn,
  DataTableEmpty,
  DataTablePagination,
} from "components/DataTable";
import CardLink from "components/CardLink";
import GameLink from "components/GameLink";
import GradeReportLink from "components/GradeReportLink";
import SetLink from "components/SetLink";
import { finalGradeText } from "components/Tables/CardViewTable";

import { PaginatedResponse } from "types/v2/catalog";
import { GradingSearchResultDto } from "types/v2/gradings";

type Grading = GradingSearchResultDto;

/*
  A factory rather than a constant because every link carries the hero's `onClear`: a
  visitor who follows a result has left the search, so the overlay closes behind them.
  Not one column declares an accessor — that is D19, expressed where it cannot be
  forgotten.
*/
export const searchCardsRowColumns = (
  onClear?: () => void
): readonly DataTableColumn<Grading>[] => [
  {
    id: "gradedId",
    label: "Grade #",
    /* The shared LINK lives in GradeReportLink; this table does not restyle it (D25). */
    render: (row) => <GradeReportLink gradedId={row.gradedId} onClick={onClear} />,
    className: "w-32",
  },
  {
    id: "game",
    label: "Game",
    render: (row) => (
      <GameLink cardInfo={row.cardInfo} game={row.game} onClick={onClear} />
    ),
    className: "w-40",
  },
  {
    id: "cardName",
    label: "Card Name",
    render: (row) => (
      <CardLink cardInfo={row.cardInfo} cardName={row.cardName} onClick={onClear} />
    ),
  },
  {
    id: "setName",
    label: "Set Name",
    render: (row) => (
      <SetLink cardInfo={row.cardInfo} setName={row.setName} onClick={onClear} />
    ),
  },
  {
    id: "finalGrade",
    label: "Grade",
    /*
      NOT `numeric`: the cell paints "10 (Gem Mint)", and numeric would run it through
      formatCount. There is nothing to sort, so the value is only ever rendered.
    */
    render: (row) => <span>{finalGradeText(row)}</span>,
    className: "w-36",
  },
];

export interface SearchCardsRowTableProps {
  /* RTK Query's response, straight through. Undefined until the first page lands. */
  data?: PaginatedResponse<Grading[]>;
  isFetching: boolean;
  /* RTK Query's error. The hero used to render its own line above the table (§12.20). */
  error?: unknown;
  /* Closes the hero overlay when a visitor follows a result. */
  onClear?: () => void;
  page: number;
  /* Fixed: it is the `limit` the hero asks the server for. */
  rowsPerPage: number;
  onChangePage: (page: number) => void;
}

function SearchCardsRowTable(props: SearchCardsRowTableProps) {
  const { data, isFetching, error, onClear, page, rowsPerPage, onChangePage } = props;

  const columns = useMemo(() => searchCardsRowColumns(onClear), [onClear]);

  const rows = data?.data ?? [];
  /* The server's post-filter total, which is what pagination has to count through. */
  const count = data?.pagination?.total ?? 0;

  return (
    <div className="flex flex-col px-3">
      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.gradedId}
        caption="Search results"
        /*
          No sort prop and no onSortChange: the header renders plain <th>s and the stacked
          list renders no sort control (D19).
        */
        isLoading={isFetching}
        error={error}
        emptyState={
          <DataTableEmpty
            title="No results found"
            description="No graded card matches this search."
          />
        }
        /* Page-sized, so the overlay does not resize as the page lands. */
        skeletonRows={rowsPerPage}
      />
      {/*
        No onChangeRowsPerPage — that omission IS the no-rows-per-page variant. The page
        size is the server `limit`, so there is nothing a visitor could choose.
      */}
      <DataTablePagination
        page={page}
        rowsPerPage={rowsPerPage}
        count={count}
        onChangePage={onChangePage}
        label="Search results pagination"
      />
    </div>
  );
}

export default SearchCardsRowTable;
